/**
 * CEPI Agents — workstream contract types (plan Task 11, spec §3.2/§3.3/§6.2/§8.1/§8.4).
 *
 * These mirror `backend/app/schemas/agent_workstream_schemas.py` and
 * `backend/app/agents/synthesis.py`'s `Report.to_json`.
 *
 * ONE OMISSION IS LOAD-BEARING, not an oversight:
 *
 * **`StepSummary` / `ReportFailure` have no `error_internal` field.** Spec
 * §8.5 keeps the redacted user message and the internal diagnostic as two
 * different things; only `error_user_message` is user-facing. The backend
 * never serializes the internal one — nothing here should invite it back.
 *
 * `PlanStep.requirements` USED to be a second such omission, on the ground
 * that a step's `[{corpus, tool}]` codes were never rendered. That changed on
 * 2026-08-31 (user decision): the plan editor lets an owner choose a step's
 * corpora and their tools directly, and an editor cannot round-trip a field
 * the API refuses to send. What survives is the real guarantee — a `code` is
 * an option VALUE and a dispatch identifier, never visible text. `label`
 * (from `/workstreams/capabilities`) is the only thing a user reads, and the
 * read-only view still shows the API's rendered `sources` line.
 */

// ---------------------------------------------------------------------------
// Workstream + steps
// ---------------------------------------------------------------------------

/** Terminal + non-terminal workstream statuses as the backend writes them. */
export type WorkstreamStatus =
  | 'drafting'
  | 'awaiting_approval'
  | 'queued'
  | 'running'
  | 'complete'
  | 'complete_with_flags'
  | 'failed'
  | 'cancelled'
  | 'infeasible';

/** Per-step statuses (spec §8.4's outcome table). */
export type StepStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'flagged'
  | 'failed'
  | 'skipped'
  | 'cancelled';

export const TERMINAL_WORKSTREAM_STATUSES: ReadonlySet<string> = new Set<WorkstreamStatus>([
  'complete',
  'complete_with_flags',
  'failed',
  'cancelled',
  'infeasible',
]);

/**
 * One materialised step. `searched_corpora` is spec §8.1's ABSENCE SCOPE and
 * carries three distinguishable states that must never collapse into one:
 *   - a non-empty array  → we searched these corpora;
 *   - an empty array     → we searched, and found nothing;
 *   - `undefined`/absent → no absence scope was recorded at all.
 * "We searched and found nothing" and "we never searched" are different claims.
 */
export interface StepSummary {
  kind?: 'selection' | 'analysis' | null;
  analysis_phase?: AnalysisPhase | null;
  analysis_coverage?: AnalysisCoverage | null;
  ordinal: number;
  goal: string;
  status: StepStatus | string;
  attempt: number;
  scope: Record<string, unknown>;
  success_criterion: string;
  continue_on_failure: boolean;
  /** Non-null when this step was carried forward from an earlier plan version. */
  carried_from_plan_version: number | null;
  cached?: boolean;
  cache_source_completed_at?: string | null;
  error_code: string | null;
  error_user_message: string | null;
  error_class: string | null;
  next_retry_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  /** Spec §8.4: a skipped step is NEVER rendered without its reason. */
  skip_reason: string | null;
  flag_reasons: string[];
  searched_corpora?: string[];
}

export interface ActionRequiredReason {
  code: 'compatibility_reapproval_required';
  message: string;
}

export interface WorkstreamSummary {
  id: string;
  status: WorkstreamStatus | string;
  request_text: string;
  created_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
  approved_plan_version: number | null;
  infeasible_reason: string | null;
  action_required_reason: ActionRequiredReason | null;
  cancel_requested: boolean;
  synthesis_status: string | null;
  report_generated_at: string | null;
  sse_seq: number;
  is_owner: boolean;
}

export interface WorkstreamDetail extends WorkstreamSummary {
  page_context: Record<string, unknown> | null;
  approved_plan_hash: string | null;
  approved_by: string | null;
  approved_at: string | null;
  latest_plan_version: number | null;
  steps: StepSummary[];
}

export interface WorkstreamListResponse {
  workstreams: WorkstreamSummary[];
}

// ---------------------------------------------------------------------------
// Plan versions
// ---------------------------------------------------------------------------

/**
 * One step of a plan version's content.
 *
 * NOTE THE ABSENT `requirements` FIELD — see the module docstring. Everything
 * here is domain language: a goal, its scope, and how we will know it is done.
 */
export interface PlanStep {
  kind?: 'selection' | 'analysis';
  goal: string;
  /**
   * The literal search terms this step runs. A top-level step field, NOT a
   * `scope` key — `scopeEntries` would never have shown it, which is why it
   * was invisible on the plan surface until 2026-08-31 despite the planner
   * emitting it and its own schema comment saying a reviewer edits it.
   * Free-form and owner-editable before approval.
   */
  query?: string;
  /** Human-rendered sources, e.g. `["US Labels (Read document)"]`. Display
   *  only — the READ view's sentence for what `requirements` says
   *  structurally. Absent for a step that consults no source. */
  sources?: string[] | null;
  /** The step's structured sources — the EDITING surface behind `sources`.
   *  Sent back verbatim on an edit; `create_plan_version` re-derives
   *  requirements only for a step that has none. */
  requirements?: PlanRequirement[];
  scope: Record<string, unknown>;
  success_criterion: string;
  continue_on_failure?: boolean;
}

/** One `(corpus, tool)` pair. Both are CODES — option values, never text. */
export interface PlanRequirement {
  corpus: string;
  tool: string;
}

// ---------------------------------------------------------------------------
// Frozen research analysis declaration
// ---------------------------------------------------------------------------

export type AnalysisScalarKind = 'string' | 'boolean' | 'integer' | 'number' | 'enum';
export type AnalysisScalarValue = string | boolean | number;

export interface AnalysisEnumOption {
  value: string;
  definition: string;
}

export interface AnalysisEvidenceRequirement {
  description: string;
  allowed_corpora: string[];
  version_scope: 'approved' | 'current' | 'origin' | 'verified_origin';
  coverage: 'passage' | 'complete_document';
  complete_document_values: AnalysisScalarValue[];
  /** Literal phrases (1-20) whose presence would answer the field; enables `not_found`. */
  search_terms?: string[];
}

export interface AnalysisFieldDefinition {
  name: string;
  label: string;
  definition: string;
  value_type: AnalysisScalarKind;
  is_list: boolean;
  units: string | null;
  allowed_values: AnalysisEnumOption[];
  evidence: AnalysisEvidenceRequirement;
  applicability: string;
  classification_criteria: string;
}

export interface AnalysisScope {
  population: string;
  topic: string;
  time_scope: string;
  approval_scope: string;
  version_scope: string;
  inclusion_criteria: string[];
  exclusion_criteria: string[];
}

export interface AnalysisRequestedDimension {
  label: string;
  description: string;
  field_names: string[];
  exclusion_reason: string | null;
}

export interface AnalysisRecordGranularity {
  label: string;
  dimension_fields: string[];
}

export interface AnalysisAnswerRules {
  unknown: string;
  unsupported: string;
  conflicting: string;
  inapplicable: string;
}

export interface AnalysisPrerequisite {
  field_name: string;
  allowed_values: Array<AnalysisScalarValue | AnalysisScalarValue[]>;
  /** True lets an accepted `not_found` on this prerequisite's field satisfy the rule. */
  accept_not_found?: boolean;
}

export interface AnalysisClassificationRule {
  target_field: string;
  target_value: boolean | string;
  description: string;
  prerequisites: AnalysisPrerequisite[];
}

export interface AnalysisContract {
  contract_version: 1;
  research_scope: AnalysisScope;
  analysis_instructions: string;
  requested_dimensions: AnalysisRequestedDimension[];
  record_granularity: AnalysisRecordGranularity;
  fields: AnalysisFieldDefinition[];
  answer_rules: AnalysisAnswerRules;
  classification_rules: AnalysisClassificationRule[];
}

export type AnalysisAnswerState =
  | 'supported'
  | 'unknown'
  | 'unsupported'
  | 'conflicting'
  | 'inapplicable'
  | 'not_found';
export type AnalysisValue = AnalysisScalarValue | AnalysisScalarValue[];

export interface AnalysisSourceQuote {
  source_key: string;
  quoted_span: string;
}

export interface AnalysisAlternative<T extends AnalysisValue = AnalysisValue> {
  value: T;
  source_quotes: AnalysisSourceQuote[];
}

export type AnalysisAnswerEnvelope<T extends AnalysisValue = AnalysisValue> =
  | {
      state: 'supported';
      value: T;
      reason: null;
      source_quotes: AnalysisSourceQuote[];
      alternatives: [];
    }
  | {
      state: 'conflicting';
      value: null;
      reason: string;
      source_quotes: AnalysisSourceQuote[];
      alternatives: [AnalysisAlternative<T>, AnalysisAlternative<T>, ...AnalysisAlternative<T>[]];
    }
  | {
      state: 'unknown' | 'unsupported' | 'inapplicable' | 'not_found';
      value: null;
      reason: string;
      source_quotes: AnalysisSourceQuote[];
      alternatives: [];
      /** Search receipts (§4.2); present only when `state === 'not_found'`. */
      searches?: ContractSearchReceipt[];
    };

export interface AnalysisInclusion {
  disposition: 'included' | 'excluded' | 'undetermined';
  reason: string;
  source_quotes: AnalysisSourceQuote[];
}

export interface ContractAnswerAlternative {
  value: AnalysisValue;
  source_quotes: AnalysisSourceQuote[];
  excerpt_ids: string[];
}

/**
 * One document's search receipt on a `not_found` answer (spec §4.2). `version`
 * carries only the keys the source provenance had; `presentation_key` is
 * added only for `eu_history` (spec: "EU history receipts include the
 * searched part and presentation").
 */
export interface ContractSearchReceiptVersion {
  version_number?: number;
  version_date?: string;
  is_true_origin?: boolean;
  submission_type?: string;
  submission_number?: string;
  review_completion_date?: string;
  issued_date?: string;
}

export interface ContractSearchReceipt {
  corpus: string;
  document_id: number;
  title: string | null;
  version_anchor: string;
  version: ContractSearchReceiptVersion;
  document_part: string | null;
  presentation_key?: string;
  terms: string[];
  call_ids: number[];
  units_scanned: number;
  characters_scanned: number;
}

export interface ContractAnswerEnvelope {
  state: AnalysisAnswerState;
  value: AnalysisValue | null;
  reason: string | null;
  source_quotes: AnalysisSourceQuote[];
  excerpt_ids: string[];
  alternatives: ContractAnswerAlternative[];
  /** Search receipts (§4.2); present only when `state === 'not_found'`. */
  searches?: ContractSearchReceipt[];
}

export interface ContractInclusion extends AnalysisInclusion {
  excerpt_ids: string[];
}

export interface ContractReportRecord {
  record_key: string;
  subject_key: string;
  drug_id: number;
  /** The `drugs`-row product name; null when that row's names are blank. */
  product_name: string | null;
  dimensions: Record<string, ContractAnswerEnvelope>;
  inclusion: ContractInclusion;
  fields: Record<string, ContractAnswerEnvelope>;
  row_ids: string[];
  identity_refinements: Array<{ from_record_key: string; to_record_key: string }>;
}

export interface ContractReportExcerpt {
  excerpt_id: string;
  quoted_span: string;
  document_type: string;
  source_label: string | null;
  ref?: Partial<ExcerptCitation>;
  [key: string]: unknown;
}

export interface ContractEvidenceRow {
  record_key: string;
  subject_key: string;
  dimensions: Record<string, ContractAnswerEnvelope>;
  excerpts: ContractReportExcerpt[];
}

export interface ContractFieldStateCounts {
  supported: number;
  unknown: number;
  unsupported: number;
  conflicting: number;
  inapplicable: number;
  included_supported: number;
  included_true?: number;
  /** Omitted by projections saved before `not_found` shipped; render as 0. */
  not_found?: number;
  included_not_found?: number;
}

export interface ContractReportProjection {
  template: { template_id: 'research_contract_v1'; version: 1 };
  analysis_contract: AnalysisContract;
  coverage: {
    contract: { drugs: number; records: number; included: number; excluded: number; undetermined: number };
    analysis: Record<string, unknown>;
  };
  slots: {
    field_states: {
      slot_id: 'field_states';
      label: string;
      unit: 'record';
      fields: Record<string, ContractFieldStateCounts>;
    };
  };
  tables: {
    all_records: { table_id: 'all_records'; title: string; columns: string[]; rows: ContractReportRecord[] };
  };
  appendices: {
    evidence: { appendix_id: 'evidence'; title: string; columns: string[]; rows: ContractEvidenceRow[] };
  };
  case_studies: null;
}

// ---------------------------------------------------------------------------
// Capability catalog (GET /api/v2/workstreams/capabilities)
// ---------------------------------------------------------------------------

/** One dropdown option: a dispatch `code` and the `label` a user reads. */
export interface CapabilityOption {
  code: string;
  label: string;
  description?: string | null;
}

/**
 * What the plan editor may offer, built server-side from the same `_MATRIX`
 * tier-1 validation reads. `matrix[corpus]` is the tools THAT corpus
 * supports, so a pair the backend would reject cannot be composed here.
 */
export interface CapabilityCatalog {
  corpora: CapabilityOption[];
  tools: CapabilityOption[];
  matrix: Record<string, string[]>;
  ha_document_types?: CapabilityValueOption[];
  /** Report-split spec §6.1: the server registry is the ONLY source of
   *  template ids. The editor offers exactly these and hardcodes none. */
  report_templates?: ReportTemplateOption[];
}

/**
 * One selectable report template. `version` travels with the id because the
 * pin an approval covers is the PAIR (spec §6.2) — the id alone would let a
 * registry revision change what an approved plan means.
 */
export interface ReportTemplateOption {
  template_id: string;
  version: number;
  label: string;
}

export interface CapabilityValueOption {
  value: string;
  label: string;
}

/** Exact public audit record returned only by the lazy step audit endpoint. */
export interface ToolCallAudit {
  attempt: number;
  call_ordinal: number;
  retry_of: number | null;
  tool_name: string;
  tool_label: string;
  corpus_label: string | null;
  document_type_labels: string[];
  tool_contract_version: number | null;
  args: Record<string, unknown>;
  status: string;
  result: Record<string, unknown> | null;
  legacy_payload_only: boolean;
  retrieved_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface CandidateReference {
  corpus: string;
  corpus_label?: string;
  drug_id: number | null;
  document_id: number | null;
  version_anchor: string;
  anchor_kind: string;
  section_number: string | null;
  letter_part: string | null;
  page: number | null;
  chunk_id: number | null;
  retrieved_at: string;
  deep_link: string;
  matching_text?: string | null;
}

export interface CandidateOutput {
  candidate_count: number;
  read_outcomes?: {
    documents_indexed: number;
    sections_read: number;
    empty_reads: number;
    unresolved_targets: number;
    reused_read_requests: number;
    read_mode: 'navigation' | 'content' | 'mixed';
  };
  candidates: { drug_id: number; drug_name: string; references: CandidateReference[] }[];
  unassociated_references: CandidateReference[];
  partial: boolean;
  capped: boolean;
  unresolved_matches: boolean;
}

export interface StepToolCallsResponse {
  workstream_id: string;
  approved_plan_version: number;
  step_ordinal: number;
  step_attempt?: number;
  output_source_step_id: number | null;
  retrieved_at: string;
  execution?: Record<string, unknown>;
  calls: ToolCallAudit[];
  history_calls: ToolCallAudit[];
  candidate_output?: CandidateOutput | null;
}

export interface PlanContent {
  contract?: 'cohort_analysis_v1';
  analysis_contract?: AnalysisContract;
  analysis_budget?: AnalysisBudget;
  /**
   * The AUTHORITATIVE pin the approval covers (spec §6.2). Server-written
   * ONLY: `plan_service.create_plan_version` strips any client-supplied value
   * before resolving, so writing this from the editor accomplishes nothing.
   * Read-only here, and read-only on screen.
   */
  report_template?: { template_id: string; version: number };
  /**
   * The only template key a client may write: an id, never a version. The
   * server resolves it to the pin above and drops the request; an unknown id
   * pins nothing and is named as a violation rather than silently defaulted.
   */
  report_template_request?: { template_id: string };
  steps: PlanStep[];
  [key: string]: unknown;
}

/** One entity the bounded tier-2 probe did NOT reach (spec §3.3). */
export interface UnprobedEntity {
  key: string;
  value: string;
}

/**
 * `unprobed_scope` as `plan_service.revalidate_plan_version` writes it.
 * `tier2_ran === false` means the bounded probe never ran for this version at
 * all — which is a stronger "unvalidated" than a non-empty `entities` list.
 */
export interface UnprobedScope {
  entities?: UnprobedEntity[];
  tier2_ran?: boolean;
}

export interface CapabilityValidation {
  violations?: string[];
}

export interface PlanVersionResponse {
  workstream_id: string;
  version: number;
  origin: string;
  content: PlanContent;
  content_hash: string;
  capability_validation: CapabilityValidation;
  unprobed_scope: UnprobedScope;
  approval_bindings: Record<string, unknown>;
  created_by: string;
  created_at: string | null;
  is_approved: boolean;
  is_approvable: boolean;
  available_versions: number[];
}

export interface ApprovePlanResponse {
  workstream_id: string;
  status: string;
  version: number;
  content_hash: string | null;
  approved_by: string | null;
  approved_at: string | null;
  steps_materialized: number;
}

// ---------------------------------------------------------------------------
// Report (synthesis.py Report.to_json)
// ---------------------------------------------------------------------------

/**
 * One evidence citation. `retrieved_at` is REQUIRED because spec §6.2 makes
 * staleness a disclosed property: resumed work reuses persisted results, so
 * every reference must say when it was actually observed. `binding_time`
 * distinguishes a reference bound at approval from one bound at runtime
 * (spec §3.4 drift).
 */
export interface EvidenceCitation {
  ref_key: string;
  drug_name?: string | null;
  corpus: string;
  family?: string | null;
  document_id?: string | number | null;
  drug_id?: string | number | null;
  section_number?: string | null;
  letter_part?: string | null;
  page?: number | null;
  chunk_id?: number | null;
  quoted_span?: string | null;
  deep_link?: string | null;
  anchor_kind: string;
  anchor_display: string;
  anchor_precision?: string | number | null;
  version_anchor?: string | number | null;
  binding_time: 'approval' | 'runtime' | string;
  retrieved_at: string;
  data_as_of?: string | null;
  diff_origin?: string | null;
  verifier_status?: string | null;
  /** `chunk_only` offsets address persisted chunk text, never a page span. */
  citation_alignment?: 'source_aligned' | 'chunk_only' | null;
  citation_alignment_reason?: string | null;
  coordinate_space?: string | null;
  text_basis?: string | null;
  source_checksum?: string | null;
}

/** A raw evidence HANDLE — never a quoted passage (synthesis `_handle_json`). */
export interface EvidenceHandle {
  kind: string;
  corpus: string;
  document_id?: string | null;
  drug_id?: string | null;
  deep_link?: string | null;
  anchor_kind: string;
  anchor_display: string;
  anchor_precision?: string | number | null;
  /**
   * Within-document locator. `read_file` returns ONE handle per matched
   * (sub)section, and `anchor_display` is only the version — so without these
   * a label read across seven sections rendered as seven identical rows.
   * Optional: a listing handle (list_files_for_drug) has no section.
   */
  section_number?: string | null;
  letter_part?: string | null;
  page?: number | null;
  binding_time: 'approval' | 'runtime' | string;
  retrieved_at: string;
}

/** `error_internal` is deliberately absent — see the module docstring. */
export interface ReportFailure {
  attempt: number | null;
  error_code: string | null;
  user_message: string | null;
  error_class: string | null;
  next_retry_at: string | null;
  recorded_at: string | null;
  is_final: boolean;
}

export interface ReportFinding {
  claim_id: string;
  text: string;
  verified: boolean;
  citations: EvidenceCitation[];
}

export interface ReportVerification {
  status: string;
  flag_reasons: string[];
  skip_reason: string | null;
  drift_notes: string[];
  /** Spec §8.1 absence scope — see `StepSummary.searched_corpora`. */
  searched_corpora: string[];
  /** False when no absence scope was recorded: "we never searched". */
  absence_scope_recorded: boolean;
}

export interface ReportStepSection {
  ordinal: number;
  goal: string;
  status: StepStatus | string;
  findings: ReportFinding[];
  citations: EvidenceCitation[];
  verification: ReportVerification;
  partial_evidence: EvidenceHandle[];
  partial_evidence_retained: boolean;
  failure: ReportFailure | null;
  attempt_failures: ReportFailure[];
  carried_from_plan_version: number | null;
  runtime_bindings: Record<string, unknown>;
  notes: string[];
  /** Optional only for reports serialized before pipeline dataset metadata. */
  query?: string;
  scope?: Record<string, unknown>;
  sources?: string[];
  execution?: Record<string, unknown>;
}

export interface SynthesisClaim {
  text: string;
  supported: boolean;
  flag_reason: string | null;
  citations: EvidenceCitation[];
}

export interface SynthesisCaveat {
  text: string;
  step_ordinal: number;
  valid: boolean;
  flag_reason: string | null;
}

/**
 * A synthesis body as persisted. `summary` / `claims` / `caveats` are OPTIONAL
 * because a **templated** body (`TemplatedReportBody` below) carries none of
 * them: it has a section tree instead. The absence of `claims` is the branch
 * discriminator the renderer uses (spec §8.3, AC10) — so this type must be able
 * to express a body without them rather than assert one that is always there.
 */
export interface SynthesisContent {
  summary?: string;
  unavailable_reason?: string | null;
  claims?: SynthesisClaim[];
  caveats?: SynthesisCaveat[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Templated report body (spec §15) — `report_writer.TemplateReportResult
// .to_json()` plus the envelope `synthesis.py` adds when it assembles the
// report. Read those two before changing a field name here.
// ---------------------------------------------------------------------------

/** One node of `report_writer._section_index`. FLAT — `level` carries the
 *  nesting, and blocks are deliberately not transported.
 *
 * THIS IS THE SPINE, NOT THE BODY. `_section_index` is what feeds a model
 * request (shipping the oncology template's hundreds of fixed-text blocks in
 * every chunk would spend the input budget restating text the model must not
 * rewrite), so it is deliberately block-free. The renderer's copy is
 * `TemplatedBodySection` below, from `report_writer.report_sections`. */
export interface TemplatedSection {
  section_id: string;
  number: string;
  title: string;
  level: number;
}

/**
 * One block of the DECLARED body (`report_writer.report_sections`).
 *
 * `kind` decides where `value` resolves, and the kinds are not interchangeable:
 *   - `text`      — `value` IS the literal text; nothing resolves it.
 *   - `prose`     — a DECLARED prose slot id, matched against
 *                   `TemplatedSlot.template_slot_id` (a repeating section
 *                   additionally matches `subject_key`).
 *   - `computed`  — `projection.slots[value]`.
 *   - `table`     — `projection.tables[value]`.
 *   - `appendix`  — `projection.appendices[value]`.
 *   - `field`     — `section.subjects[i].fields[value]`; `label` is what a
 *                   reader sees. A `null` value is UNSUPPORTED (§9.1 — never
 *                   guessed), which is not the same as absent.
 *   - `figure`    — an id only; nothing is generated for it.
 */
export type TemplatedBlockKind =
  | 'text'
  | 'prose'
  | 'computed'
  | 'table'
  | 'appendix'
  | 'field'
  | 'figure';

export interface TemplatedBlock {
  kind: TemplatedBlockKind | string;
  value: string;
  label: string | null;
}

/** One entry of a repeating section's DECLARED selection (§10). The selection
 *  is the projection's `case_studies` rule — never the model's. */
export interface TemplatedSubject {
  subject_key: string;
  /** The one record (instrument) this subject's `fields` were read from. */
  instrument_key: string;
  cohort: string;
  /** The `field` blocks' values for this subject. `null` = unsupported. */
  fields: Record<string, unknown>;
  /** Rule 18a: resolved at read time by `GET /report` from `subject_key`
   *  (`drug:<id>` only), the same lookup already used for citation display
   *  names. Absent/null when the subject is not a `drug:<id>` key or the
   *  lookup found nothing — the `fields.product_name` value (itself
   *  possibly unsupported) is what renders then. */
  drug_name?: string | null;
}

/** One entry of `report_writer.report_sections` — the body the renderer walks.
 *  Flat pre-order, `level` carries the depth, in template order.
 *
 *  PROSE IS NOT THE BODY: `oncology_pro_v1`'s "Objective" is fixed text plus
 *  five computed counts and NOT ONE prose slot, so a renderer walking only the
 *  spine and the written prose leaves it numbered, titled and empty. */
export interface TemplatedBodySection extends TemplatedSection {
  /** Non-null = render `blocks` once per entry of `subjects`, in order. */
  repeat: string | null;
  subjects: TemplatedSubject[];
  blocks: TemplatedBlock[];
}

/** One `WrittenSlot.to_json()`. The two reference lists are the TWO REFERENCE
 *  KINDS of §11.5 and are not interchangeable: `excerpt_ids` resolve against
 *  the pinned snapshot's citation register, `computed_slot_ids` against the
 *  pinned projection's `slots`. */
export interface TemplatedSlot {
  slot_id: string;
  /** The DECLARED slot this was written for. A repetition's `slot_id` is
   *  `"<template_slot_id>#<subject_key>"`, so only this puts it back under the
   *  block the template placed. OPTIONAL: a body written before
   *  `report_sections` landed carries none, and `slot_id` is then the id. */
  template_slot_id?: string;
  /** The subject a repeated case block was written about; null when the
   *  section does not repeat. */
  subject_key?: string | null;
  /** Non-empty for a slot that declares a cardinality — and then it, NOT
   *  `text`, is the slot's content (`_TemplateProseSlot.items`; `text`
   *  defaults to ""). */
  items?: string[];
  section_id: string;
  text: string;
  scope: string;
  excerpt_ids: string[];
  computed_slot_ids: string[];
  supported: boolean;
  flag_reason: string | null;
  placeholder: boolean;
}

/**
 * One entry of the flat citation register — `{excerpt_id, ...EvidenceRef.to_json()}`.
 *
 * It has NO `ref_key` and NO `anchor_display`: `EvidenceRef.to_json()` emits
 * neither, and `excerpt_id` is what prose cites and what every consumer looks
 * the entry up by after the report is re-read from the database. `drug_name` is
 * resolved at read time by `GET /report`, never taken from writer text.
 */
export interface ExcerptCitation extends Omit<EvidenceCitation, 'ref_key' | 'anchor_display'> {
  excerpt_id: string;
}

export interface ProjectionPercentage {
  numerator: number;
  denominator: number;
  denominator_slot: string | null;
  unit: string;
  value: number;
  /** Already carries its denominator, e.g. `12.5% (3/24 subjects)` (§10). */
  display: string;
}

export interface ProjectionDenominator {
  slot_id?: string | null;
  unit: string;
  value: number;
  basis: string;
}

/** One entry of `projection.slots` — what a COMPUTED-SLOT reference resolves
 *  to. `op` is the discriminator its renderer switches on; a `percentage_of`
 *  slot's `value` is a percentage, a `count` slot's is a count, and reading one
 *  as the other misstates the number. */
export interface ComputedSlotValue {
  slot_id: string;
  op: string;
  label: string;
  unit?: string;
  value?: number | null;
  denominator?: ProjectionDenominator | null;
  percentage?: ProjectionPercentage | null;
  rows?: Record<string, unknown>[];
  /** The DISTINCT subjects behind a `subject`-unit count, in snapshot order —
   *  the same set `count_units` reduced to `value`, so this list and that
   *  number can never disagree. `null`/absent for any other unit: a record or
   *  document count is not a count of subjects. */
  subject_keys?: string[] | null;
  [key: string]: unknown;
}

export interface ProjectionTable {
  table_id: string;
  title: string;
  columns: string[];
  rows: { slot_id: string; label: string; cells: unknown[] }[];
}

export interface ProjectionAppendix {
  appendix_id: string;
  title: string;
  source: string;
  op: string;
  columns: string[];
  fields: string[];
  rows: unknown[][];
  counts: Record<string, number>;
}

/** One entry of `projection.references` — a curated FDA guidance/communication
 *  addressable by `ref_id`, mirroring the backend's `REFERENCE_FIELDS`
 *  (`report_template.py`) exactly: nine plain-string fields, no more. It
 *  addresses no document the way `EvidenceRef` does (no corpus, no section,
 *  no quoted span) — it is a citable entry in a curated list, resolved
 *  through the same computed-reference address space as a `ComputedSlotValue`. */
export interface ReferenceEntry {
  ref_id: string;
  title: string;
  first_issued: string;
  issuer: string;
  document_type: string;
  dates: string;
  status: string;
  summary: string;
  url: string;
}

/** `projection.project_report`'s envelope — its module docstring is the
 *  contract. Deterministic: no model call produced any of it. */
export interface ReportProjection {
  template?: { template_id: string; version: number };
  analysis_contract?: AnalysisContract;
  coverage?: Record<string, unknown>;
  slots: Record<string, ComputedSlotValue>;
  tables: Record<string, ProjectionTable>;
  appendices: Record<string, ProjectionAppendix>;
  case_studies?: Record<string, unknown> | null;
  results?: Record<string, unknown>;
  /** Only present when `template.references` is non-empty (`project_report`);
   *  keyed by `ref_id`, same map for every entry regardless of how many
   *  slots cite it. */
  references?: Record<string, ReferenceEntry>;
}

export interface TemplatedReportBody {
  status: string;
  /** Rule 15 / decision D2: the one-line reader-facing explanation of why
   *  `status` landed where it did (e.g. "12 of 35 citations are unverified").
   *  `null`/absent when the status needs no such line. Rides alongside
   *  `status` in the synthesis JSON (`report_writer.TemplateReportResult.
   *  to_json`); never a second status enum. */
  status_disclosure?: string | null;
  title: string;
  slots: TemplatedSlot[];
  citations: ExcerptCitation[];
  limitations: string[];
  requests_used: number;
  chunks: Record<string, unknown>[];
  report_template: { template_id: string; version: number };
  /** The block-free SPINE. Present on every body; not what the page renders. */
  section_tree: TemplatedSection[];
  /** The DECLARED body, blocks included. OPTIONAL only because a body written
   *  before `report_sections` landed has none — the renderer then falls back
   *  to `section_tree` and shows prose alone, which is what this key exists to
   *  stop being the normal case. */
  sections?: TemplatedBodySection[];
  projection: ReportProjection;
  resumed_from_snapshot: boolean;
  digest_disclosure: string;
  /** Rule 18a's lookup, applied to computed slots' `subject_keys`: one
   *  read-time `drug:<id>` → display-name map for the whole report. Absent on
   *  a report assembled before this landed; a key it does not carry renders
   *  as itself. */
  subject_display?: Record<string, string>;
}

export interface WorkstreamReport {
  contract?: 'cohort_analysis_v1';
  analysis_coverage?: AnalysisCoverage | null;
  workstream_id: string;
  plan_version: number;
  request_text: string;
  workstream_status: WorkstreamStatus | string;
  synthesis: SynthesisContent | null;
  synthesis_status: string | null;
  synthesis_flags: Record<string, unknown>[];
  digest_reduction: Record<string, unknown>;
  /** Spec §5.4: reduction that is not STATED is silent reduction. Always shown. */
  reduction_disclosure: string;
  approval_bindings: Record<string, unknown>;
  sections: ReportStepSection[];
}

export type AnalysisPhase = 'acquisition' | 'analysis' | 'report' | 'complete';
export interface AnalysisCoverage {
  selected_candidates: number;
  analyzed_candidates: number;
  unresolved_candidates: number;
  /** Rule 16 top-of-report coverage statement (Task 5/8): products SELECTED
   *  for this cohort but never analyzed at all — a set disjoint from
   *  `unresolved_candidates` (`agent_workstream_routes.py:_report_coverage_
   *  extras`). Optional: absent on a report predating this rule, or on the
   *  step-progress projection, which never carries it. */
  never_analyzed_count?: number;
  /** Of `unresolved_candidates`, how many were analyzed anyway — the other
   *  half of the same two-fact disclosure. Never combine these two counts
   *  into one merged "not analyzed" number (rule 16). */
  analyzed_and_unresolved_count?: number;
}
export interface AnalysisBudget {
  cogni_search: number;
  investigator: number;
  read_file: number;
  acquisition: number;
  map: number;
  final: number;
  context_tokens: number;
  output_tokens: number;
}

// ---------------------------------------------------------------------------
// SSE envelope (orchestrator.build_workstream_event)
// ---------------------------------------------------------------------------

export type WorkstreamEventType = 'status' | 'step' | 'drift' | 'error' | 'planning';

export interface WorkstreamEvent {
  seq: number;
  event: WorkstreamEventType | string;
  workstream_id: string;
  payload: Record<string, unknown>;
  emitted_at: string;
}

/**
 * Payload of a `planning` event (spec §4.1). A distinct event type — not a
 * `status` event with a planner-specific payload — because the hook folds
 * `status` events into `snapshot.status`; routing planner phases through it
 * would mean emitting "status changes" where the lifecycle status does not
 * change. `detail` is display text and must never contain an internal tool
 * name — the planner is responsible for keeping it clean before emission; no
 * shared validator checks this field.
 */
export interface PlanningPayload {
  phase: 'understanding' | 'probing' | 'reviewing' | 'finalizing' | 'plan_review' | 'writing';
  detail?: string;
}

/** The `snapshot` frame: a `WorkstreamSummary` plus its materialised steps. */
export interface WorkstreamSnapshot extends WorkstreamSummary {
  steps: StepSummary[];
}
